import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, RefreshCw, Users, DollarSign, MapPin, CheckCircle2, Search } from "lucide-react";

const SLUG = "regional-conference-2026";

interface CheckinRow {
  id: string;
  registration_id: string;
  member_id: string | null;
  region: string;
  city: string | null;
  category: string | null;
  attendee_name: string | null;
  age_group: string | null;
  paid_at_checkin: boolean;
  amount_collected: number | null;
  payment_method: string | null;
  checked_in_by: string | null;
  checked_in_at: string;
  registration_type: "online" | "onsite" | null;
}

interface CheckinSummaryRow {
  region: string | null;
  city: string | null;
  category: string | null;
  checked_in: number | string | null;
  paid_at_checkin: number | string | null;
  amount_collected: number | string | null;
}

interface CheckinTotals {
  checked_in: number | string | null;
  paid_at_checkin: number | string | null;
  unpaid_at_checkin: number | string | null;
  amount_collected: number | string | null;
  cities: number | string | null;
  onsite: number | string | null;
  online: number | string | null;
  adults: number | string | null;
  teens: number | string | null;
  children_6_12: number | string | null;
  children_0_5: number | string | null;
}

interface RegistrationFallbackRow {
  id: string;
  region: string | null;
  city: string | null;
  category: string | null;
  payment_status: string | null;
  total_members: number | string | null;
  adults_18_plus: number | string | null;
  teenagers_13_17: number | string | null;
  children_6_12: number | string | null;
  children_0_5: number | string | null;
  amount_paid: number | string | null;
  category_price: number | string | null;
  created_at: string | null;
}

interface Props {
  userRole?: string;
  userRegionName?: string | null;
  userRegionId?: string | null;
}

const normalizeRegion = (value?: string | null) =>
  (value || "")
    .toLowerCase()
    .replace(/\bregion\b/g, "")
    .replace(/[^a-z]/g, "")
    .trim();

const regionMatches = (a?: string | null, b?: string | null) => {
  const left = normalizeRegion(a);
  const right = normalizeRegion(b);
  return Boolean(left && right && (left === right || left.includes(right) || right.includes(left)));
};

const toNumber = (value: number | string | null | undefined) => Number(value) || 0;

const isPaidRegistration = (row: RegistrationFallbackRow) => {
  const status = (row.payment_status || "").toLowerCase();
  return status === "confirmed" || status === "paid" || status === "completed";
};

const attendeeCount = (row: RegistrationFallbackRow) => {
  const total = toNumber(row.total_members);
  if (total > 0) return total;
  const ageTotal = toNumber(row.adults_18_plus) + toNumber(row.teenagers_13_17) + toNumber(row.children_6_12) + toNumber(row.children_0_5);
  return ageTotal > 0 ? ageTotal : 1;
};

export default function CheckinDashboard({ userRole, userRegionName, userRegionId }: Props) {
  const [rows, setRows] = useState<CheckinRow[]>([]);
  const [summaryRows, setSummaryRows] = useState<CheckinSummaryRow[]>([]);
  const [totals, setTotals] = useState<CheckinTotals | null>(null);
  const [registrationFallbackRows, setRegistrationFallbackRows] = useState<RegistrationFallbackRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resolvedRegionName, setResolvedRegionName] = useState<string | null>(userRegionName || null);
  const [q, setQ] = useState("");
  const isGlobal = userRole === "global_admin" || userRole === "president";
  const scopeRegion = isGlobal ? null : resolvedRegionName || userRegionName || null;

  const loadRegistrationFallback = async () => {
    const { data, error } = await (supabase.from("conference_registrations" as any) as any)
      .select("id, region, city, category, payment_status, total_members, adults_18_plus, teenagers_13_17, children_6_12, children_0_5, amount_paid, category_price, created_at")
      .eq("conference_slug", SLUG)
      .order("created_at", { ascending: false })
      .limit(5000);

    if (error) {
      console.error("[checkin-dashboard] Registration fallback query failed", error);
      setRegistrationFallbackRows([]);
      setErrorMessage((prev) => prev || error.message || "Unable to load registration fallback totals.");
      return;
    }

    const rawRows = ((data as RegistrationFallbackRow[]) || []);
    const scopedRows = scopeRegion ? rawRows.filter((row) => regionMatches(row.region, scopeRegion)) : rawRows;
    setRegistrationFallbackRows(scopedRows);
  };

  const load = async () => {
    setRefreshing(true);
    setErrorMessage(null);

    // RLS already scopes non-global admins to their region. The RPC returns
    // database-side aggregate counts, so dashboard numbers are not capped by
    // the recent-row table query.
    const [detailResult, summaryResult, totalsResult] = await Promise.all([
      (supabase.from("conference_checkins" as any) as any)
        .select("*")
        .eq("conference_slug", SLUG)
        .order("checked_in_at", { ascending: false })
        .limit(500),
      (supabase as any).rpc("admin_checkin_summary", {
        p_slug: SLUG,
        p_region: scopeRegion,
      }),
      (supabase as any).rpc("admin_checkin_totals", {
        p_slug: SLUG,
        p_region: scopeRegion,
      }),
    ]);

    if (detailResult.error) {
      console.error("[checkin-dashboard] Detail query failed", detailResult.error);
      setErrorMessage(detailResult.error.message || "Unable to load recent check-ins.");
    } else {
      setRows((detailResult.data as CheckinRow[]) || []);
    }

    if (summaryResult.error) {
      console.error("[checkin-dashboard] Summary query failed", summaryResult.error);
      setErrorMessage((prev) => prev || summaryResult.error.message || "Unable to load check-in totals.");
      setSummaryRows([]);
    } else {
      setSummaryRows((summaryResult.data as CheckinSummaryRow[]) || []);
    }

    let liveCheckinTotal = rows.length;

    if (totalsResult.error) {
      console.error("[checkin-dashboard] Totals query failed", totalsResult.error);
      setErrorMessage((prev) => prev || totalsResult.error.message || "Unable to load check-in KPI totals.");
      setTotals(null);
    } else {
      const nextTotals = Array.isArray(totalsResult.data) ? totalsResult.data[0] : null;
      liveCheckinTotal = toNumber((nextTotals as CheckinTotals | null)?.checked_in);
      setTotals((nextTotals as CheckinTotals) || null);
    }

    if (liveCheckinTotal === 0 && (!detailResult.data || detailResult.data.length === 0) && (!summaryResult.data || summaryResult.data.length === 0)) {
      await loadRegistrationFallback();
    } else {
      setRegistrationFallbackRows([]);
    }

    setLoading(false);
    setRefreshing(false);
  };


  useEffect(() => {
    let cancelled = false;
    const resolveRegionName = async () => {
      if (isGlobal) {
        setResolvedRegionName(null);
        return;
      }
      if (userRegionName) {
        setResolvedRegionName(userRegionName);
        return;
      }
      if (!userRegionId) return;
      const { data, error } = await supabase
        .from("regions")
        .select("name")
        .eq("id", userRegionId)
        .maybeSingle();
      if (!cancelled && !error && data?.name) setResolvedRegionName(data.name as string);
    };
    void resolveRegionName();
    return () => { cancelled = true; };
  }, [isGlobal, userRegionId, userRegionName]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scopeRegion]);

  // Realtime — refresh on every new check-in
  useEffect(() => {
    const ch = supabase
      .channel("conference-checkins-live")
      .on("postgres_changes", { event: "*", schema: "public", table: "conference_checkins" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
    // eslint-disable-next-line
  }, [scopeRegion]);

  const ageBucket = (a: string | null): "adult" | "teen" | "child_6_12" | "child_0_5" | "other" => {
    const v = (a || "").toLowerCase().trim();
    if (!v) return "other";
    if (v.startsWith("adult") || v.includes("18")) return "adult";
    if (v.startsWith("teen") || v.includes("13")) return "teen";
    if (v.includes("6_12") || v.includes("6-12")) return "child_6_12";
    if (v.includes("0_5") || v.includes("0-5") || v.startsWith("child")) return "child_0_5";
    return "other";
  };

  const stats = useMemo(() => {
    const hasLiveCheckins = (totals ? toNumber(totals.checked_in) > 0 : false) || summaryRows.length > 0 || rows.length > 0;

    if (!hasLiveCheckins && registrationFallbackRows.length > 0) {
      const total = registrationFallbackRows.reduce((s, row) => s + attendeeCount(row), 0);
      const paidAtCheckin = registrationFallbackRows.filter(isPaidRegistration).length;
      const unpaidAtCheckin = Math.max(0, registrationFallbackRows.length - paidAtCheckin);
      const collected = registrationFallbackRows.reduce((s, row) => {
        const paid = toNumber(row.amount_paid);
        return s + (paid > 0 ? paid : (isPaidRegistration(row) ? toNumber(row.category_price) : 0));
      }, 0);
      const cities = new Set(registrationFallbackRows.map((row) => (row.city || "Unknown").trim() || "Unknown")).size;
      const adults = registrationFallbackRows.reduce((s, row) => s + toNumber(row.adults_18_plus), 0);
      const teens = registrationFallbackRows.reduce((s, row) => s + toNumber(row.teenagers_13_17), 0);
      const kids612 = registrationFallbackRows.reduce((s, row) => s + toNumber(row.children_6_12), 0);
      const kids05 = registrationFallbackRows.reduce((s, row) => s + toNumber(row.children_0_5), 0);
      const children = kids612 + kids05;
      return { total, paidAtCheckin, unpaidAtCheckin, collected, cities, onsite: 0, online: registrationFallbackRows.length, onsiteCollected: 0, adults, teens, children, kids612, kids05 };
    }

    if (totals) {
      const total = Number(totals.checked_in) || 0;
      const paidAtCheckin = Number(totals.paid_at_checkin) || 0;
      const unpaidAtCheckin = Number(totals.unpaid_at_checkin) || Math.max(0, total - paidAtCheckin);
      const collected = Number(totals.amount_collected) || 0;
      const cities = Number(totals.cities) || 0;
      const onsite = Number(totals.onsite) || 0;
      const online = Number(totals.online) || Math.max(0, total - onsite);
      const adults = Number(totals.adults) || 0;
      const teens = Number(totals.teens) || 0;
      const kids612 = Number(totals.children_6_12) || 0;
      const kids05 = Number(totals.children_0_5) || 0;
      const children = kids612 + kids05;
      return { total, paidAtCheckin, unpaidAtCheckin, collected, cities, onsite, online, onsiteCollected: collected, adults, teens, children, kids612, kids05 };
    }

    const summaryTotal = summaryRows.reduce((s, r) => s + (Number(r.checked_in) || 0), 0);
    const total = summaryRows.length > 0 ? summaryTotal : rows.length;
    const paidAtCheckin = summaryRows.length > 0
      ? summaryRows.reduce((s, r) => s + (Number(r.paid_at_checkin) || 0), 0)
      : rows.filter((r) => r.paid_at_checkin).length;
    const collected = summaryRows.length > 0
      ? summaryRows.reduce((s, r) => s + (Number(r.amount_collected) || 0), 0)
      : rows.reduce((s, r) => s + (Number(r.amount_collected) || 0), 0);
    const cities = summaryRows.length > 0
      ? new Set(summaryRows.map((r) => (r.city || "Unknown").trim())).size
      : new Set(rows.map((r) => (r.city || "Unknown").trim())).size;
    const onsite = rows.filter((r) => r.registration_type === "onsite").length;
    const online = total - onsite;
    const onsiteCollected = rows
      .filter((r) => r.registration_type === "onsite")
      .reduce((s, r) => s + (Number(r.amount_collected) || 0), 0);
    let adults = 0, teens = 0, kids612 = 0, kids05 = 0;
    for (const r of rows) {
      const b = ageBucket(r.age_group);
      if (b === "adult") adults++;
      else if (b === "teen") teens++;
      else if (b === "child_6_12") kids612++;
      else if (b === "child_0_5") kids05++;
    }
    const children = kids612 + kids05;
    const unpaidAtCheckin = Math.max(0, total - paidAtCheckin);
    return { total, paidAtCheckin, unpaidAtCheckin, collected, cities, onsite, online, onsiteCollected, adults, teens, children, kids612, kids05 };
  }, [rows, summaryRows, totals, registrationFallbackRows]);

  const usingRegistrationFallback = useMemo(() => {
    const hasLiveCheckins = (totals ? toNumber(totals.checked_in) > 0 : false) || summaryRows.length > 0 || rows.length > 0;
    return !hasLiveCheckins && registrationFallbackRows.length > 0;
  }, [registrationFallbackRows.length, rows.length, summaryRows.length, totals]);

  const byCity = useMemo(() => {
    const m = new Map<string, { count: number; paid: number; onsite: number; adults: number; teens: number; children: number }>();
    if (usingRegistrationFallback) {
      for (const row of registrationFallbackRows) {
        const c = (row.city || "Unknown").trim() || "Unknown";
        const cur = m.get(c) || { count: 0, paid: 0, onsite: 0, adults: 0, teens: 0, children: 0 };
        cur.count += attendeeCount(row);
        if (isPaidRegistration(row)) cur.paid++;
        cur.adults += toNumber(row.adults_18_plus);
        cur.teens += toNumber(row.teenagers_13_17);
        cur.children += toNumber(row.children_6_12) + toNumber(row.children_0_5);
        m.set(c, cur);
      }
      return Array.from(m.entries()).sort((a, b) => b[1].count - a[1].count);
    }
    if (summaryRows.length > 0) {
      for (const r of summaryRows) {
        const c = (r.city || "Unknown").trim() || "Unknown";
        const cur = m.get(c) || { count: 0, paid: 0, onsite: 0, adults: 0, teens: 0, children: 0 };
        cur.count += Number(r.checked_in) || 0;
        cur.paid += Number(r.paid_at_checkin) || 0;
        m.set(c, cur);
      }
      return Array.from(m.entries()).sort((a, b) => b[1].count - a[1].count);
    }
    for (const r of rows) {
      const c = (r.city || "Unknown").trim() || "Unknown";
      const cur = m.get(c) || { count: 0, paid: 0, onsite: 0, adults: 0, teens: 0, children: 0 };
      cur.count++;
      if (r.paid_at_checkin) cur.paid++;
      if (r.registration_type === "onsite") cur.onsite++;
      const b = ageBucket(r.age_group);
      if (b === "adult") cur.adults++;
      else if (b === "teen") cur.teens++;
      else if (b === "child_6_12" || b === "child_0_5") cur.children++;
      m.set(c, cur);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].count - a[1].count);
  }, [rows, summaryRows, registrationFallbackRows, usingRegistrationFallback]);

  const byCategory = useMemo(() => {
    const m = new Map<string, { total: number; adults: number; teens: number; children: number }>();
    if (usingRegistrationFallback) {
      for (const row of registrationFallbackRows) {
        const c = (row.category || "unspecified").toLowerCase();
        const cur = m.get(c) || { total: 0, adults: 0, teens: 0, children: 0 };
        cur.total += attendeeCount(row);
        cur.adults += toNumber(row.adults_18_plus);
        cur.teens += toNumber(row.teenagers_13_17);
        cur.children += toNumber(row.children_6_12) + toNumber(row.children_0_5);
        m.set(c, cur);
      }
      return Array.from(m.entries()).sort((a, b) => b[1].total - a[1].total);
    }
    if (summaryRows.length > 0) {
      for (const r of summaryRows) {
        const c = (r.category || "unspecified").toLowerCase();
        const cur = m.get(c) || { total: 0, adults: 0, teens: 0, children: 0 };
        cur.total += Number(r.checked_in) || 0;
        m.set(c, cur);
      }
      return Array.from(m.entries()).sort((a, b) => b[1].total - a[1].total);
    }
    for (const r of rows) {
      const c = (r.category || "unspecified").toLowerCase();
      const cur = m.get(c) || { total: 0, adults: 0, teens: 0, children: 0 };
      cur.total++;
      const b = ageBucket(r.age_group);
      if (b === "adult") cur.adults++;
      else if (b === "teen") cur.teens++;
      else if (b === "child_6_12" || b === "child_0_5") cur.children++;
      m.set(c, cur);
    }
    return Array.from(m.entries()).sort((a, b) => b[1].total - a[1].total);
  }, [rows, summaryRows, registrationFallbackRows, usingRegistrationFallback]);

  const filtered = useMemo(() => {
    if (!q.trim()) return rows.slice(0, 200);
    const t = q.toLowerCase();
    return rows.filter(
      (r) =>
        (r.attendee_name || "").toLowerCase().includes(t) ||
        (r.city || "").toLowerCase().includes(t) ||
        (r.category || "").toLowerCase().includes(t) ||
        (r.region || "").toLowerCase().includes(t),
    ).slice(0, 300);
  }, [rows, q]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Conference Check-in Dashboard
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Live check-ins from <a href="https://checin.ofuesi.org" target="_blank" rel="noopener" className="text-sky-700 underline">checin.ofuesi.org</a>
              {scopeRegion ? <> — scoped to <b>{scopeRegion}</b></> : <> — all regions</>}.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {errorMessage && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMessage}
            </div>
          )}
          {usingRegistrationFallback && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              No live check-in rows have reached this database yet, so the dashboard is showing the live registration roster totals until the check-in portal syncs.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={<Users className="w-4 h-4" />} label={usingRegistrationFallback ? "Registered attendees" : "Checked in"} value={stats.total} tone="sky" />
            <KPI icon={<DollarSign className="w-4 h-4" />} label={usingRegistrationFallback ? "Paid registrations" : "Paid at check-in"} value={stats.paidAtCheckin} tone="emerald" />
            <KPI icon={<DollarSign className="w-4 h-4" />} label={usingRegistrationFallback ? "Unpaid registrations" : "Not paid at check-in"} value={stats.unpaidAtCheckin} tone="amber" />
            <KPI icon={<MapPin className="w-4 h-4" />} label="Cities represented" value={stats.cities} tone="indigo" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <KPI icon={<Users className="w-4 h-4" />} label={usingRegistrationFallback ? "Families / registrations" : "Online registrations"} value={stats.online} tone="sky" />
            <KPI icon={<Users className="w-4 h-4" />} label="Onsite registrations" value={stats.onsite} tone="emerald" />
            <KPI icon={<DollarSign className="w-4 h-4" />} label={usingRegistrationFallback ? "Confirmed fee value" : "Fees collected"} value={`$${stats.collected.toFixed(0)}`} tone="amber" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={<Users className="w-4 h-4" />} label="Adults (18+)" value={stats.adults} tone="indigo" />
            <KPI icon={<Users className="w-4 h-4" />} label="Teens (13–17)" value={stats.teens} tone="sky" />
            <KPI icon={<Users className="w-4 h-4" />} label="Children (6–12)" value={stats.kids612} tone="emerald" />
            <KPI icon={<Users className="w-4 h-4" />} label="Children (0–5)" value={stats.kids05} tone="amber" />
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Check-ins by City</CardTitle></CardHeader>
          <CardContent>
            {byCity.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No check-ins yet.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-auto pr-1">
                {byCity.map(([city, v]) => (
                  <div key={city} className="flex items-center justify-between text-sm border-b last:border-b-0 py-1.5">
                    <span className="font-medium">{city}</span>
                    <span className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge variant="secondary">{v.count}</Badge>
                      {v.adults > 0 && <Badge variant="outline">A:{v.adults}</Badge>}
                      {v.teens > 0 && <Badge variant="outline">T:{v.teens}</Badge>}
                      {v.children > 0 && <Badge variant="outline">C:{v.children}</Badge>}
                      {v.onsite > 0 && <Badge className="bg-indigo-600">Onsite: {v.onsite}</Badge>}
                      {v.paid > 0 && <Badge className="bg-emerald-600">{usingRegistrationFallback ? "Paid regs" : "Paid on-site"}: {v.paid}</Badge>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Check-ins by Registration Category</CardTitle></CardHeader>
          <CardContent>
            {byCategory.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">No check-ins yet.</p>
            ) : (
              <div className="space-y-2 max-h-80 overflow-auto pr-1">
                {byCategory.map(([cat, v]) => (
                  <div key={cat} className="flex items-center justify-between text-sm border-b last:border-b-0 py-1.5">
                    <span className="capitalize font-medium">{cat.replace(/_/g, " ")}</span>
                    <span className="flex items-center gap-2 flex-wrap justify-end">
                      <Badge variant="secondary">{v.total}</Badge>
                      {v.adults > 0 && <Badge variant="outline">A:{v.adults}</Badge>}
                      {v.teens > 0 && <Badge variant="outline">T:{v.teens}</Badge>}
                      {v.children > 0 && <Badge variant="outline">C:{v.children}</Badge>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Recent Check-ins</CardTitle>
          <div className="relative w-64 max-w-full">
            <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/city…" className="pl-8 h-9" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto border rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left">
                  <th className="p-2">Time</th>
                  <th className="p-2">Attendee</th>
                  <th className="p-2">Type</th>
                  <th className="p-2">City</th>
                  <th className="p-2">Region</th>
                  <th className="p-2">Category</th>
                  <th className="p-2">Age</th>
                  <th className="p-2">Paid on-site</th>
                  <th className="p-2">By</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="p-6 text-center text-muted-foreground italic">No check-ins match.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t hover:bg-muted/20">
                    <td className="p-2 whitespace-nowrap">{new Date(r.checked_in_at).toLocaleString()}</td>
                    <td className="p-2">{r.attendee_name || "—"}</td>
                    <td className="p-2">
                      {r.registration_type === "onsite" ? (
                        <Badge className="bg-indigo-600">Onsite</Badge>
                      ) : (
                        <Badge variant="secondary">Online</Badge>
                      )}
                    </td>
                    <td className="p-2">{r.city || "—"}</td>
                    <td className="p-2">{r.region}</td>
                    <td className="p-2 capitalize">{(r.category || "").replace(/_/g, " ") || "—"}</td>
                    <td className="p-2 capitalize">{(r.age_group || "").replace(/_/g, " ") || "—"}</td>
                    <td className="p-2">
                      {r.paid_at_checkin ? (
                        <Badge className="bg-emerald-600">
                          Yes{r.amount_collected ? ` · $${Number(r.amount_collected).toFixed(0)}` : ""}
                        </Badge>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2">{r.checked_in_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function KPI({ icon, label, value, tone }: { icon: React.ReactNode; label: string; value: React.ReactNode; tone: "sky" | "emerald" | "amber" | "indigo" }) {
  const toneMap: Record<string, string> = {
    sky: "from-sky-50 to-sky-100 border-sky-200 text-sky-900",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber: "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    indigo: "from-indigo-50 to-indigo-100 border-indigo-200 text-indigo-900",
  };
  return (
    <div className={`rounded-xl border bg-gradient-to-br p-4 ${toneMap[tone]}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">{icon}{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
    </div>
  );
}
